// ============================================================
// PlanUploadCard — déposer un plan et déclencher sa lecture.
//
// Étape 1 de la boucle d'apprentissage. Le corpus réel n'est pas
// « un plan = un fichier » : c'est un PDF de vingt pages mélangeant
// page de garde, plans et nomenclatures. Le découpage est fait par le
// serveur au dépôt, et chaque page devient une ligne lisible
// individuellement — c'est ce qui rend le coût attribuable et ce qui
// évite qu'une page illisible entraîne les autres.
//
// Plusieurs plans se déposent d'un coup (29 octobre 2026), par la
// sélection ou en les glissant sur la carte : voir `depotFichiers.js`.
// ============================================================

import { useCallback, useRef, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api, LONG_TIMEOUT_MS, READ_TIMEOUT_MS } from '../../lib/api.js';
import { fileDeDepot, deposerEnFile, bilanDepot } from './depotFichiers.js';
import DepotsEnCours from './DepotsEnCours.jsx';

export function PlanUploadCard({ devisId, disabled, onLu, onDepose, onAvantSuppression, onSupprime }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [depots, setDepots] = useState([]);
  const [survol, setSurvol] = useState(false);

  // Les pages ET les lectures déjà faites : une page lue se signale, et
  // ne se relit pas toute seule. Deux lectures de la même page peuvent
  // différer — le banc de variance mesure au moins 4,8 % de divergence
  // structurée — et relire à chaque ouverture d'écran donnerait à voir
  // cette instabilité au lieu de la contenir. Sans compter le coût.
  const load = useCallback(
    () =>
      Promise.all([
        api.get(`/devis/${devisId}/plans`),
        api.get(`/devis/${devisId}/propositions`),
      ]).then(([plans, props]) => ({
        ...plans,
        lues: new Map((props.propositions ?? []).filter((p) => p.file_id).map((p) => [p.file_id, p])),
      })),
    [devisId],
  );
  const { data, error, loading, reload } = useResource(load);

  // Plusieurs fichiers, envoyés EN FILE (voir `depotFichiers.js`) : un
  // refus garde sa raison sur sa ligne et n'arrête pas les suivants.
  async function deposer(fichiers) {
    const file = fileDeDepot(fichiers);
    if (!file.length) return;
    setBusy('upload');
    setDepots(file);
    try {
      const fin = await deposerEnFile(file, async (f) => {
        // Les octets bruts, pas de multipart : le serveur vérifie les
        // octets magiques et choisit lui-même le nom de stockage.
        const res = await api.postBinary(`/devis/${devisId}/plans`, await f.arrayBuffer(), {
          filename: f.name,
          // JPEG, PNG et TIFF acceptés depuis le 6 octobre 2026 : le serveur les convertit en PDF.
          type: f.type || 'application/octet-stream',
          timeout: LONG_TIMEOUT_MS,
        });
        // Le serveur a déjà lancé la lecture : on suit dès le premier plan.
        if (res.traitement?.lancees) onDepose?.();
        return res;
      }, setDepots);
      const refus = fin.some((e) => e.etat === 'refuse');
      (refus ? toast.error : toast.success)(bilanDepot(fin));
      if (!refus) setDepots([]);
      reload();
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function read(fileId, { relire = false } = {}) {
    if (relire && !window.confirm(
      'Relire cette page ? Une nouvelle lecture coûte un appel au modèle et peut donner un '
        + 'résultat différent de celui conservé.',
    )) return;
    setBusy(fileId);
    try {
      const res = await api.post(
        `/devis/${devisId}/plans/fichier/${fileId}/lecture${relire ? '?relire=1' : ''}`,
        undefined,
        { timeout: READ_TIMEOUT_MS },
      );
      onLu?.();
      if (!res.from_cache) reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  }

  // ── LE PLAN PART AVEC SES PIÈCES — 27 octobre 2026 (devis 2026-10-036) ──
  // Les pièces restaient, détachées : un redépôt en créait une de plus, et
  // l'écran gardait la page effacée, d'où un 500 à chaque enregistrement.
  // On enregistre AVANT, le serveur nomme le travail perdu (409), et on
  // recharge le devis APRÈS — sans le réenregistrer.
  async function removeDocument(fileId) {
    if (!window.confirm('Supprimer ce document, ses pages et les pièces lues dessus ?')) return;
    setBusy(fileId);
    try {
      await onAvantSuppression?.();
      const url = `/devis/${devisId}/plans/${fileId}`;
      await api.delete(url, { body: { confirmer_perte: false } }).catch((err) => {
        if (err.status !== 409 || !err.details?.confirmation_requise || !window.confirm(err.message)) throw err;
        return api.delete(url, { body: { confirmer_perte: true } });
      });
      reload();
      onSupprime?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Card>
        <CardHead title="Plans">
          <Button
            size="sm"
            variant="primary"
            disabled={disabled || busy === 'upload'}
            onClick={() => inputRef.current?.click()}
          >
            {busy === 'upload' ? 'Dépôt…' : 'Déposer des plans'}
          </Button>
        </CardHead>
        <CardBody className="stack">
          <div className={survol ? 'depot-zone depot-zone--survol' : 'depot-zone'}>
            Glissez ici un ou plusieurs plans (PDF, JPEG, PNG, TIFF), ou cliquez pour les choisir.
            {/* Le champ COUVRE la zone, transparent : lâcher des fichiers sur
                un champ fichier les sélectionne nativement, sans gestionnaire
                de glisser-déposer à recoder. */}
            <input
              ref={inputRef}
              id={`plan-upload-${devisId}`}
              className="depot-zone__input"
              aria-label="Déposer un ou plusieurs plans (PDF ou image)"
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png,image/tiff"
              disabled={disabled || busy === 'upload'}
              onChange={(e) => deposer(e.target.files)}
              onDragEnter={() => setSurvol(true)}
              onDragLeave={() => setSurvol(false)}
              onDrop={() => setSurvol(false)}
            />
          </div>
          <DepotsEnCours file={depots} />
          {loading ? <SkeletonRows rows={2} /> : null}
          {error ? <ErrorState error={error} onRetry={reload} /> : null}
          {/* L'état vide reste explicite : c'est le premier geste du
              programme, et un écran muet ne dit pas quoi faire. Une fois
              un plan déposé, il disparaît — il n'a plus rien à dire. */}
          {!loading && !error && (data?.documents ?? []).length === 0 ? (
            <EmptyState title="Aucun plan déposé">
              Déposez le PDF du client. Il sera découpé page par page, et chaque page se lit
              séparément.
            </EmptyState>
          ) : null}

          {/* ── Les documents déposés se replient ──
              Un document déposé n'a plus rien à dire : ses pages sont
              lues, et c'est la bande de pièces qui porte la suite. Cette
              liste repoussait le PLAN — ce qu'on vient voir — à 900 px
              du haut de l'écran. Elle reste à un clic, avec son bouton
              de suppression. */}
          <details className="repli" open={(data?.documents ?? []).length === 0}>
          <summary>
            {(data?.documents ?? []).length} document(s) déposé(s) — voir et supprimer
          </summary>
          {(data?.documents ?? []).map((doc) => (
            <div key={doc.id} className="plan-doc">
              <div className="row row--between">
                <p className="plan-doc__name">{doc.original_name}</p>
                <div className="row">
                  <Badge tone="neutral">{doc.pages.length} page(s)</Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={disabled || busy === doc.id}
                    onClick={() => removeDocument(doc.id)}
                  >
                    Supprimer
                  </Button>
                </div>
              </div>
              <ul className="plan-pages">
                {doc.pages.map((page) => {
                  const lue = data?.lues?.get(page.id) ?? null;
                  return (
                    <li className="plan-pages__item" key={page.id}>
                      {/* Plus de bouton « voir » : la page se choisit en
                          choisissant sa PIÈCE, dans la bande du haut.
                          Deux façons de sélectionner, c'étaient deux
                          états qui divergeaient. */}
                      <span className="plan-pages__num">Page {page.page_number}</span>
                      {lue ? <Badge tone="ok">lue</Badge> : null}
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={disabled || busy === page.id}
                        onClick={() => read(page.id)}
                      >
                        {busy === page.id ? 'Lecture…' : lue ? 'Voir la lecture' : 'Lire'}
                      </Button>
                      {lue ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={disabled || busy === page.id}
                          onClick={() => read(page.id, { relire: true })}
                        >
                          Relire
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </details>
        </CardBody>
      </Card>

      {/* Le visualiseur et le panneau de lecture ont QUITTÉ cette
          carte. Ils vivaient ici avec leur propre état de page, à côté
          d'un état de lecture indépendant — et les deux pouvaient
          diverger sans que rien ne le signale. Ils sont désormais
          gouvernés par la sélection unique de l'écran, sur la pièce.
          Voir CLAUDE.md §11, treizième contrôle. */}
    </>
  );
}

export default PlanUploadCard;
